import type { User } from 'firebase/auth';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { mmkvStorage } from '@/stores/middleware/mmkv-storage';

export type SessionUser = User | null;

/** Roles a user can have. A single account may hold more than one. */
export type UserRole = 'coach' | 'guardian';

export type SessionState = {
  user: SessionUser;
  authToken: string | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
  /** All roles resolved for the signed-in user (coach/{uid}, parent/{uid} existence). */
  roles: UserRole[];
  /** The role flow currently active. Null when the user must still pick (multi-role). */
  activeRole: UserRole | null;
  isPendingLoginSocial?: boolean;
  /**
   * "Soft" logout: the user tapped logout inside the app but we keep the Firebase session and
   * `user` warm so they can re-enter quickly via the biometric/password login screen. While
   * true the auth gates (`withAuth`/`withUnAuth`) treat the app as locked and route to
   * `LoginWithBiometrics`. Persisted (the only persisted field of this store) so a
   * killed-then-reopened app with a still-valid session lands back on the lock screen.
   * Cleared only on an explicit re-auth (biometric / password / social) or a hard sign-out.
   */
  isSoftLoggedOut: boolean;
};

export type SessionActions = {
  setSessionUser: (user: SessionUser) => void;
  setSessionAuthToken: (authToken: string | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setRoles: (roles: UserRole[]) => void;
  setActiveRole: (activeRole: UserRole | null) => void;
  resetSessionState: () => void;
  setSessionInitialized: (isInitialized: boolean) => void;
  setIsPendingLoginSocial: (isPending: boolean) => void;
  /** Flip the soft-logout lock. `true` locks to the biometric login screen; `false` unlocks. */
  setSoftLoggedOut: (isSoftLoggedOut: boolean) => void;
};

type SessionStore = SessionState & SessionActions;

const initialState: SessionState = {
  user: null,
  authToken: null,
  isInitialized: false,
  isAuthenticated: false,
  roles: [],
  activeRole: null,
  isSoftLoggedOut: false,
};

export const useSessionStore = create<SessionStore>()(
  persist(
    set => ({
      ...initialState,

      resetSessionState: () => {
        set({
          ...initialState,
          isInitialized: true,
        });
      },

      setSessionAuthToken: (authToken: string | null) => {
        set({
          authToken,
        });
      },
      setIsAuthenticated: (isAuthenticated: boolean) => {
        set({
          isAuthenticated,
        });
      },

      setSessionUser: (user: SessionUser) => {
        set({
          user,
        });
      },
      setRoles: (roles: UserRole[]) => {
        set({
          roles,
        });
      },
      setActiveRole: (activeRole: UserRole | null) => {
        set({
          activeRole,
        });
      },
      setSessionInitialized: (isInitialized: boolean) => {
        set({
          isInitialized,
        });
      },

      setIsPendingLoginSocial: (isPending: boolean) => {
        set({
          isPendingLoginSocial: isPending,
        });
      },

      setSoftLoggedOut: (isSoftLoggedOut: boolean) => {
        set({
          isSoftLoggedOut,
        });
      },
    }),
    {
      name: 'session-store',
      storage: createJSONStorage(() => mmkvStorage),
      // Persist ONLY the soft-logout lock. The Firebase user/token/roles are restored on
      // launch via AuthStateEffect (and `user` isn't serializable), so keeping just this flag
      // lets a killed-then-reopened app land back on the biometric login screen.
      partialize: state => ({ isSoftLoggedOut: state.isSoftLoggedOut }),
    },
  ),
);

export const selectSessionState = (state: SessionStore): SessionState => ({
  user: state.user,
  authToken: state.authToken,
  isInitialized: state.isInitialized,
  isAuthenticated: state.isAuthenticated,
  roles: state.roles,
  activeRole: state.activeRole,
  isSoftLoggedOut: state.isSoftLoggedOut,
});

export const selectSessionActions = (state: SessionStore): SessionActions => ({
  setSessionUser: state.setSessionUser,
  setSessionAuthToken: state.setSessionAuthToken,
  setRoles: state.setRoles,
  setActiveRole: state.setActiveRole,
  resetSessionState: state.resetSessionState,
  setSessionInitialized: state.setSessionInitialized,
  setIsAuthenticated: state.setIsAuthenticated,
  setIsPendingLoginSocial: state.setIsPendingLoginSocial,
  setSoftLoggedOut: state.setSoftLoggedOut,
});
